import React, { useState, useEffect } from "react";
import {
  Paper,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import {
  LocationOn as LocationOnIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { postJsonData } from "../network/ApiController";
import ApiEndpoints from "../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";

const VisitCard = ({
  visitNumber,
  row,
  onDeleteSuccess,
  fetchLocationName,
  openImageModal,
}) => {
  const theme = useTheme();
  const [locationName, setLocationName] = useState("Loading...");
  const [locationLoading, setLocationLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  console.log("Thre row id ", row.id);
  console.log("the visit number is ", visitNumber);
  useEffect(() => {
    let isMounted = true;
    const loadLocation = async () => {
      if (row[`location${visitNumber}`]) {
        setLocationLoading(true);
        try {
          const name = await fetchLocationName(row[`location${visitNumber}`]);
          if (isMounted) setLocationName(name);
        } finally {
          if (isMounted) setLocationLoading(false);
        }
      }
    };
    loadLocation();
    return () => {
      isMounted = false;
    };
  }, [row, visitNumber, fetchLocationName]);

  const handleDelete = async () => {
    setConfirmOpen(false);

    setDeleteLoading(true);
    postJsonData(
      `${ApiEndpoints.DELETE_SALES_ATTENDANCE} `,
      { id: row?.id, visit: visitNumber },
      setDeleteLoading,
      (res) => {
        okSuccessToast(res.data.message || "Visit deleted successfully");
        onDeleteSuccess()
      },
      (error) => {
        apiErrorToast(error);
        setDeleteLoading(false);
      }
    );
  };
  const formatTime = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

  const formatDate = (dateString) =>
    dateString ? new Date(dateString).toLocaleDateString() : null;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px",
          background: theme.palette.background.paper,
          transition: "all 0.2s ease-out",
          position: "relative",
          "&:hover": {
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
            transform: "translateY(-1px)",
          },
        }}
      >
        {/* Delete Button */}
        <IconButton
          size="small"
          onClick={() => setConfirmOpen(true)}
          disabled={deleteLoading}
          sx={{
            position: "absolute",
            top: -10,
            right: -12,
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.1),
            },
          }}
        >
          {deleteLoading ? (
            <CircularProgress size={20} />
          ) : (
            <DeleteIcon fontSize="small" />
          )}
        </IconButton>

        {/* Card Content */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              src={row[`selfie${visitNumber}`]}
              sx={{
                width: 40,
                height: 40,
                border: `1.5px solid ${theme.palette.primary.light}`,
                cursor: "pointer",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
              onClick={() => openImageModal(row[`selfie${visitNumber}`])}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
                Visit {visitNumber}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row[`visit_time${visitNumber}`]
                  ? formatTime(row[`visit_time${visitNumber}`])
                  : "No time"}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={
              row[`visit_time${visitNumber}`]
                ? formatDate(row[`visit_time${visitNumber}`])
                : "No date"
            }
            size="small"
            variant="outlined"
            sx={{
              fontSize: "0.65rem",
              height: "24px",
              borderRadius: "6px",
            }}
          />
        </Box>

        <Box
          sx={{
            backgroundColor: theme.palette.grey[50],
            borderRadius: "8px",
            p: 1.2,
            mt: 0.5,
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={1}>
            {locationLoading ? (
              <CircularProgress size={16} thickness={5} sx={{ mr: 0.5 }} />
            ) : (
              <LocationOnIcon
                color="primary"
                sx={{
                  fontSize: "18px",
                  mt: "2px",
                  flexShrink: 0,
                }}
              />
            )}
            <Box flex={1}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.3,
                }}
              >
                {locationName}
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                mt={0.9}
                sx={{
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: "4px",
                  px: 0.8,
                  py: 0.3,
                  width: "fit-content",
                }}
              >
                <LocationOnIcon
                  color="secondary"
                  sx={{
                    fontSize: "18px",
                    mt: "2px",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" color="textSecondary">
                  Coordinates: {row[`location${visitNumber}`] || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this visit record?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VisitCard;
